<script lang="ts" setup>
import { ref, inject, onMounted, watch } from 'vue';

import type { Ref } from 'vue';
import type { Nullable } from '@immersive-tech/viewer-engine';
import type { IManifest, IManifestTranslation } from '@/types/IManifest';

const manifest = inject('manifest') as Ref<IManifest>;
const translationLanguages = inject('translationLanguages') as Ref<{ [key: string]: Nullable<string> }>;
const blobPath = ref<string>();

const fixCSVString = (input: Nullable<string>) => {
  if (!input) return null;

  return `"${input.replaceAll('"', '""')}"`;
};

const generateBlob = () => {
  const csv = [Object.keys(translationLanguages.value).join(',') + '\n'];
  const translationTargets = Object.keys(manifest.value.hotspots);
  const defaultTranslationLanguages = Object.values(translationLanguages.value) as string[];

  // Remove first element since it is english (null) and not a target translation language
  defaultTranslationLanguages.shift();

  for (const ctaButton of manifest.value.ctaButtons) {
    if (!ctaButton.text || ctaButton.text in translationTargets) continue;

    translationTargets.push(ctaButton.text);
  }

  for (const phrase of translationTargets) {
    if (!phrase || phrase === '' || !translationTargets.includes(phrase)) continue;

    const row = [fixCSVString(phrase)];

    for (const language of defaultTranslationLanguages) {
      row.push(fixCSVString(manifest.value.translations[phrase][language as keyof IManifestTranslation]));
    }

    csv.push(row.join(',') + '\n');
  }

  const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), csv.join('')], { type: 'text/csv; charset=utf-8' });

  blobPath.value = URL.createObjectURL(blob);
};

const manifestWatcher = () => {
  generateBlob();
};

const mountHandler = () => {
  generateBlob();
};

onMounted(mountHandler);
watch(manifest, manifestWatcher, { deep: true });
</script>

<template>
  <a :href="blobPath" :download="`translations-${manifest.name || 'tour'}.v${manifest.version}.csv`" class="cursor-pointer text-xs bg-blue-400 text-white p-1 text-center w-full my-0.5 last-of-type:mb-0 rounded-sm">
    Export Translations
  </a>
</template>
